import React from 'react';
import './LandingPage.css';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import LanguageToggle from './LanguageToggle';
import AuthControl from './AuthControl';
import SEO from './SEO';
import {
  FiCode,
  FiShield,
  FiMonitor,
  FiPlay,
  FiActivity,
  FiLayers,
  FiFileText,
  FiPrinter,
  FiArrowRight,
  FiCheck
} from "react-icons/fi";

const CONTACT_EMAIL = "sales@frontplay.dev";
const PLAN_CARDS = ["free", "light", "business"];

const LandingPage = ({ onGetStarted, isAdmin, onOpenAdmin }) => {
  const { texts } = useLanguage();
  const { tier, openPlanModal } = useAuth();

  const features = [
    {
      icon: <FiShield />,
      title: texts.landing.features.enterpriseSecurity.title,
      description: texts.landing.features.enterpriseSecurity.description
    },
    {
      icon: <FiCode />,
      title: texts.landing.features.aiCodeExecution.title,
      description: texts.landing.features.aiCodeExecution.description
    },
    {
      icon: <FiMonitor />,
      title: texts.landing.features.realtimeValidation.title,
      description: texts.landing.features.realtimeValidation.description
    },
    {
      icon: <FiPlay />,
      title: texts.landing.features.safeExecution.title,
      description: texts.landing.features.safeExecution.description
    },
    {
      icon: <FiActivity />,
      title: texts.landing.features.debugSupport.title,
      description: texts.landing.features.debugSupport.description
    },
    {
      icon: <FiLayers />,
      title: texts.landing.features.multiFormat.title,
      description: texts.landing.features.multiFormat.description
    }
  ];

  const codeExample = `<!DOCTYPE html>
<html>
<head>
  <style>
    .container {
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 100vh;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    }
    .card {
      background: white;
      padding: 2rem;
      border-radius: 20px;
      box-shadow: 0 20px 40px rgba(0,0,0,0.1);
      text-align: center;
    }
  </style>
</head>
<body>
  <div class="container">
    <div class="card">
      <h1>${texts.landing.codeExample.title}</h1>
      <p>${texts.landing.codeExample.description}</p>
      <button onclick="showMessage()">
        ${texts.landing.codeExample.buttonText}
      </button>
    </div>
  </div>
  
  <script>
    function showMessage() {
      alert('Welcome to FrontPlay!');
    }
  </script>
</body>
</html>`;

  return (
    <div className="landing-page">
      <SEO
        title={`${texts.landing.title} | ${texts.landing.subtitle1} ${texts.landing.subtitle2}`}
        description={`${texts.landing.description1} ${texts.landing.description2}${texts.landing.description3} ${texts.landing.description4 || ""}`.trim()}
        canonical="https://frontplay.contextaflex.com"
      />
      <header className="landing-header">
        <div className="landing-header-inner">
          <button
            type="button"
            className="landing-logo"
            onClick={() => {
              window.scrollTo({ top: 0, behavior: 'smooth' });
              if (window.location.hash) {
                window.history.replaceState(null, '', window.location.pathname + window.location.search);
              }
            }}
            title={texts.landing.backToTop}
          >
            {texts.landing.title}
          </button>
          <nav className="landing-nav">
            <a href="#security">{texts.landing.nav.security}</a>
            <a href="#features">{texts.landing.nav.features}</a>
            <a href="#use-cases">{texts.landing.nav.useCases}</a>
            <a href="#plans">{texts.landing.nav.plans}</a>
          </nav>
          <div className="landing-header-actions">
            {isAdmin && (
              <button type="button" className="landing-admin-btn" onClick={onOpenAdmin}>
                {(texts.admin && texts.admin.title) || "Admin"}
              </button>
            )}
            <LanguageToggle />
            <AuthControl />
          </div>
        </div>
      </header>

      <section className="hero">
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title">
              <span className="gradient-text">{texts.landing.title}</span>
              <br />
              {texts.landing.subtitle1}
              <br />
              {texts.landing.subtitle2}
            </h1>
            <p className="hero-description">
              {texts.landing.description1}
              <br />
              <strong>{texts.landing.description2}</strong>{texts.landing.description3}
            </p>
            <div className="hero-buttons">
              <button className="cta-button primary" onClick={onGetStarted}>
                {texts.landing.getStartedButton} <FiArrowRight />
              </button>
              <a href="#features" className="cta-button secondary">
                {texts.landing.featuresButton}
              </a>
            </div>
          </div>
          <div className="hero-demo">
            <div className="demo-window">
              <div className="demo-header">
                <div className="demo-dots">
                  <span className="dot red"></span>
                  <span className="dot yellow"></span>
                  <span className="dot green"></span>
                </div>
                <div className="demo-title">FrontPlay</div>
              </div>
              <div className="demo-content">
                <pre className="demo-code">
                  <code>{codeExample}</code>
                </pre>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="security" className="security-highlight">
        <div className="container">
          <div className="security-content">
            <h2 className="security-title">
              <FiShield className="security-title-icon" />
              {texts.landing.securityTitle}
            </h2>
            <p className="security-subtitle">
              {texts.landing.securitySubtitle}
            </p>
            <div className="security-features">
              <div className="security-feature">
                <div className="security-feature-icon"><FiShield /></div>
                <h3>{texts.landing.securityFeatures.sandbox.title}</h3>
                <p>{texts.landing.securityFeatures.sandbox.description}</p>
              </div>
              <div className="security-feature">
                <div className="security-feature-icon"><FiLayers /></div>
                <h3>{texts.landing.securityFeatures.apiRestriction.title}</h3>
                <p>{texts.landing.securityFeatures.apiRestriction.description}</p>
              </div>
              <div className="security-feature">
                <div className="security-feature-icon"><FiMonitor /></div>
                <h3>{texts.landing.securityFeatures.realtimeMonitoring.title}</h3>
                <p>{texts.landing.securityFeatures.realtimeMonitoring.description}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="features" className="features">
        <div className="container">
          <h2 className="section-title">{texts.landing.sectionTitle}</h2>
          <p className="section-subtitle">
            {texts.landing.sectionSubtitle}
          </p>
          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card">
                <div className="feature-icon">{feature.icon}</div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="use-cases" className="use-cases">
        <div className="container">
          <h2 className="section-title">{texts.landing.useCasesTitle}</h2>
          <p className="section-subtitle">{texts.landing.useCasesSubtitle}</p>
          <div className="use-cases-grid">
            <div className="use-case">
              <div className="use-case-icon"><FiShield /></div>
              <h3>{texts.landing.useCases.isolatedRun.title}</h3>
              <p>{texts.landing.useCases.isolatedRun.description}</p>
            </div>
            <div className="use-case">
              <div className="use-case-icon"><FiActivity /></div>
              <h3>{texts.landing.useCases.networkCheck.title}</h3>
              <p>{texts.landing.useCases.networkCheck.description}</p>
            </div>
            <div className="use-case">
              <div className="use-case-icon"><FiFileText /></div>
              <h3>{texts.landing.useCases.codeDeliverable.title}</h3>
              <p>{texts.landing.useCases.codeDeliverable.description}</p>
            </div>
            <div className="use-case">
              <div className="use-case-icon"><FiPrinter /></div>
              <h3>{texts.landing.useCases.printShare.title}</h3>
              <p>{texts.landing.useCases.printShare.description}</p>
            </div>
          </div>
        </div>
      </section>

      <section id="plans" className="plans">
        <div className="container">
          <h2 className="section-title">{texts.landing.plans.title}</h2>
          <p className="section-subtitle">{texts.landing.plans.subtitle}</p>
          <div className="plans-grid">
            {PLAN_CARDS.map((key) => {
              const plan = texts.landing.plans[key];
              if (!plan) return null;
              const isCurrent = tier === key;
              const isBusiness = key === "business";
              const isFree = key === "free";
              const ctaLabel = isCurrent
                ? (texts.billing?.currentPlan || "現在のプラン")
                : plan.cta;
              const ctaHref = isBusiness
                ? `mailto:${CONTACT_EMAIL}?subject=FrontPlay%20Business%20plan`
                : undefined;
              const ctaOnClick = isBusiness
                ? undefined
                : isFree
                ? onGetStarted
                : isCurrent
                ? undefined
                : openPlanModal;
              return (
                <div key={key} className={`plan-card plan-card--${key}`}>
                  {plan.badge ? (
                    <span className="plan-badge">{plan.badge}</span>
                  ) : null}
                  <h3 className="plan-name">{plan.name}</h3>
                  <p className="plan-price">{plan.priceLabel}</p>
                  <ul className="plan-features">
                    {plan.features.map((feature, index) => (
                      <li key={index}><FiCheck /> {feature}</li>
                    ))}
                  </ul>
                  <a
                    className="cta-button primary plan-cta"
                    href={ctaHref}
                    onClick={ctaOnClick}
                    style={isCurrent ? { opacity: 0.6, pointerEvents: "none" } : undefined}
                  >
                    {ctaLabel} {!isBusiness && <FiArrowRight />}
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">{texts.landing.ctaTitle}</h2>
            <p className="cta-description">
              {texts.landing.ctaDescription1}
            </p>
            <button className="cta-button primary large" onClick={onGetStarted}>
              {texts.landing.ctaButton} <FiArrowRight />
            </button>
          </div>
        </div>
      </section>

      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <h3>{texts.landing.footerTitle}</h3>
              <p>{texts.landing.footerDescription}</p>
            </div>
            <div className="footer-links">
              <div className="footer-section">
                <h4>{texts.landing.footerSections.security}</h4>
                <ul>
                  <li>{texts.landing.footerSections.sandboxExecution}</li>
                  <li>{texts.landing.footerSections.apiRestrictionSystem}</li>
                  <li>{texts.landing.footerSections.multiLayerDefense}</li>
                </ul>
              </div>
              <div className="footer-section">
                <h4>{texts.landing.footerSections.formatSupport}</h4>
                <ul>
                  <li>HTML/CSS/JS</li>
                  <li>React</li>
                  <li>Canvas</li>
                  <li>Markdown</li>
                </ul>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2025 eprize</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
